/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import FormTitle from "../../../form-fields/form-title";
import TextInput from "../../../form-fields/text-input";
import SelectInput from "../../../form-fields/select-input";
import DashBoardContainer from "../../../layout/dashboard-container";
import {
  FormLabel,
  FormValue,
  GridRow,
} from "../generate-receipt/custom-form-grid";
import { useSelector } from "react-redux";
import { labels } from "../../../../lang/labels";
import { useFormik, FormikProvider, Form } from "formik";
import { cancelReceiptCashierCounterSchema } from "../../../../utils/validation-schema";
import {
  cancelReceipt,
  getCancelCashierCounterReceipt,
} from "../../../../services/cancel-receipt-service";
import Loader from "../../../loader/loader";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FormButtons from "../buttons";
import { showToastError, showToastSuccess } from "../../../common/toastHelper";

const initialState = {
  receiptType: "",
  receiptNumber: "",
  remark: "",
};

const CancelReceipt = () => {
  const lang = useSelector((state) => state.userDetails.lang);
  const formik = useFormik({
    initialValues: initialState,
    validationSchema: cancelReceiptCashierCounterSchema,
    onSubmit: (values) => {
      alert(JSON.stringify(values, null, 2));
    },
  });

  const [receiptTypes, setReceiptTypes] = useState([
    { value: "BillReceipt", label: "Bill Receipt" },
    { value: "PrefilledReceipt", label: "Prefilled Receipt" },
  ]);

  const [cancelReceipts, setCancelReceipts] = useState("");
  const [receiptData, setReceiptData] = useState({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setError("");
    setCancelReceipts("");
  }, [formik.values.receiptNumber, formik.values.receiptType]);

  const handleShowClick = async (e) => {
    e.preventDefault();
    try {
      const body = {
        receiptno: formik.values.receiptNumber,
        receipttype: formik.values.receiptType,
      };
      setIsLoading(true);
      const res = await getCancelCashierCounterReceipt(body);
      showToastSuccess();
      setCancelReceipts(res?.lst);
      setReceiptData({
        receiptno: res?.receiptno,
        receipttype: res?.receipttype,
      });
      setError("");
    } catch (error) {
      if (
        error?.response?.status === 400 &&
        error?.response?.data?.code === "4001"
      ) {
        showToastError(error.response.data.message);
      } else {
        showToastError("Something went wrong, please try again!");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReceipt = async (e, item) => {
    e.preventDefault();
    formik.setTouched({
      remark: true,
    });
    await formik.validateForm();
    if (formik.values.remark) {
      try {
        const body = {
          propertycode: item.propertycode,
          propertyname: item.propertyname,
          modeofpayment: item.modeofpayment,
          receiptdate: item.receiptdate,
          paidamount: item.paidamount,
          ...receiptData,
          remark: formik.values.remark,
        };

        setIsLoading(true);
        const res = await cancelReceipt(body);
        showToastSuccess("Success: Receipt cancelled.");
      } catch (error) {
        if (
          error.response.status === 400 &&
          error.response.data.code === "4001"
        ) {
          showToastError(error.response.data.message);
        } else {
          showToastError("Something went wrong, please try again!");
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      showToastError("To cancel receipt Remark is required!");
    }
  };

  return (
    <DashBoardContainer>
      {isLoading && <Loader />}
      <FormikProvider value={formik}>
        <Form>
          {error && (
            <Alert
              sx={{
                padding: "0px",
                paddingLeft: "10px",
                paddingRight: "10px",
                marginTop: "15px",
              }}
              severity="error"
              onClose={() => {
                setError("");
              }}
            >
              {error}
            </Alert>
          )}
          <Paper elevation={4} sx={{ marginBottom: "15px" }}>
            <FormTitle title={labels.CancelReceipt[lang]} />
            <Grid>
              <GridRow>
                <FormLabel label={labels.ReceiptType[lang]} required />
                <FormValue
                  component={
                    <SelectInput
                      name="receiptType"
                      options={receiptTypes}
                      required
                    />
                  }
                />
              </GridRow>

              <GridRow>
                <FormLabel label={labels.ReceiptNumber[lang]} required />
                <FormValue
                  component={<TextInput name="receiptNumber" required />}
                />
                <FormLabel label={labels.Remark[lang]} required />
                <FormValue component={<TextInput name="remark" required />} />
              </GridRow>

              <Grid container justifyContent="center" alignItems="center">
                <Grid
                  item
                  md={3}
                  container
                  justifyContent={{ md: "flex-end" }}
                  alignItems="center"
                  p={2}
                >
                  <FormButtons
                    isValid={!(formik.isValid && formik.dirty)}
                    handleSubmitButtonClick={handleShowClick}
                    resetForm={() => {
                      formik.resetForm();
                    }}
                    submitBtnLabel="Show"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
        {cancelReceipts && (
          <GridRow>
            <TableContainer style={{ padding: "50px" }}>
              <Table
                size="small"
                sx={{ minWidth: 550 }}
                aria-label="simple table"
              >
                <TableHead>
                  <TableRow
                    sx={{
                      background: "#e6eaeb",
                      "&:last-child td, &:last-child th": {
                        border: "1px solid black",
                      },
                    }}
                  >
                    <TableCell align="center">
                      {labels.PropertyNumber[lang]}
                    </TableCell>
                    <TableCell align="center">
                      {labels.PropertyName[lang]}
                    </TableCell>
                    <TableCell align="center">
                      {labels.ReceiptDate[lang]}
                    </TableCell>
                    <TableCell align="center">
                      {labels.ReceiptNumber[lang]}
                    </TableCell>
                    <TableCell align="center">
                      {labels.PaymentMode[lang]}
                    </TableCell>
                    <TableCell align="center">{labels.Amount[lang]}</TableCell>
                    <TableCell align="center">{labels.Action[lang]}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cancelReceipts.map((item, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell align="center" className="cellBorder">
                          {item.propertycode}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          {item.propertyname}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          {item.receiptdate}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          {item.receiptno}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          {item.modeofpayment}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          {item.paidamount}
                        </TableCell>
                        <TableCell align="center" className="cellBorder">
                          <Button
                            // disabled={!formik.values.remark}
                            variant="contained"
                            size="small"
                            onClick={(e) => {
                              handleCancelReceipt(e, item);
                            }}
                            sx={{
                              background: "red",
                              "&:hover": {
                                background: "red",
                              },
                            }}
                            endIcon={<CancelIcon />}
                          >
                            Cancel
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </GridRow>
        )}
      </FormikProvider>
    </DashBoardContainer>
  );
};

export default CancelReceipt;
